import type { AIImageGenerationConfig } from '@/lib/image-generation-helpers';
import { getFreeformSectionAiConfig } from '@/lib/image-generation-helpers';
import { isPendingAIImage, normalizeImageValue } from '@/types/image';
import type { Page } from '@/types/page';
import type { PageSection } from '@/types/page-section';

export function getNestedValue<T = unknown>(obj: unknown, path: string): T | undefined {
  if (!path.includes('.')) {
    if (obj && typeof obj === 'object') {
      return (obj as Record<string, T>)[path];
    }
    return undefined;
  }

  const parts = path.split('.');
  let value: unknown = obj;
  for (const part of parts) {
    if (value == null) return undefined;
    if (typeof value !== 'object') return undefined;
    value = (value as Record<string, unknown>)[part];
  }
  return value as T | undefined;
}

function setNestedValue(
  source: Record<string, unknown>,
  path: string,
  value: unknown,
): Record<string, unknown> {
  if (!path.includes('.')) {
    return { ...source, [path]: value };
  }

  const parts = path.split('.');
  const updated = { ...source };
  let current: Record<string, unknown> = updated;

  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i];
    const existing = current[key];
    const next =
      typeof existing === 'object' && existing !== null && !Array.isArray(existing)
        ? { ...(existing as Record<string, unknown>) }
        : {};
    current[key] = next;
    current = next;
  }

  current[parts[parts.length - 1]] = value;
  return updated;
}

function buildCompletedImageValue(
  currentValue: unknown,
  imageUrl: string,
): Record<string, unknown> {
  const normalized = normalizeImageValue(currentValue);

  if (normalized) {
    return {
      ...(normalized.assetId ? { assetId: normalized.assetId } : {}),
      url: imageUrl,
      ...(normalized.alt ? { alt: normalized.alt } : {}),
      ...(normalized.isInitialProp ? { isInitialProp: normalized.isInitialProp } : {}),
    };
  }

  return { url: imageUrl };
}

/**
 * Updates section props with completed image based on AI config.
 * Handles both section-level images and array item images.
 */
export function updateImageInProps(
  props: Record<string, unknown>,
  aiConfig: AIImageGenerationConfig,
  itemId: string | null,
  imageUrl: string,
): Record<string, unknown> {
  const updatedProps = { ...props };

  // Section-level image
  if (aiConfig.sectionImage && !itemId) {
    const field = aiConfig.sectionImage.field;
    const currentValue = getNestedValue(updatedProps, field);
    return setNestedValue(updatedProps, field, buildCompletedImageValue(currentValue, imageUrl));
  }

  // Array item image
  if (aiConfig.arrayItemImages && itemId) {
    const { arrayField, imageField } = aiConfig.arrayItemImages;
    const rawArray = updatedProps[arrayField];
    const array = [...(Array.isArray(rawArray) ? rawArray : [])] as Array<Record<string, unknown>>;
    const itemIndex = array.findIndex((item) => item.id === itemId);

    if (itemIndex !== -1) {
      const currentValue = getNestedValue(array[itemIndex], imageField);
      array[itemIndex] = setNestedValue(
        array[itemIndex],
        imageField,
        buildCompletedImageValue(currentValue, imageUrl),
      );
      updatedProps[arrayField] = array;
    }
  }

  return updatedProps;
}

export function mergeResolvedImagesFromStore(incomingPage: Page, storedPage: Page): Page {
  if (!incomingPage?.slots?.main?.sections || !storedPage?.slots?.main?.sections) {
    return incomingPage;
  }

  const storedSections = new Map<string, PageSection>(
    storedPage.slots.main.sections.map((section) => [section.id, section]),
  );

  let hasChanges = false;
  const mergedSections = incomingPage.slots.main.sections.map((section) => {
    const storedSection = storedSections.get(section.id);
    if (!storedSection || !section.props || !storedSection.props) {
      return section;
    }

    const aiConfig = getFreeformSectionAiConfig(section);
    if (!aiConfig) {
      return section;
    }

    let nextProps = section.props;
    let sectionChanged = false;

    if (aiConfig.sectionImage) {
      const field = aiConfig.sectionImage.field;
      const incomingValue = getNestedValue(section.props, field);
      const storedValue = getNestedValue(storedSection.props, field);

      if (isPendingAIImage(incomingValue) && storedValue) {
        const storedUrl = imageUrlForStoredValue(storedValue);
        if (!storedUrl) {
          return section;
        }
        nextProps = setNestedValue(
          nextProps,
          field,
          buildCompletedImageValue(incomingValue, storedUrl),
        );
        sectionChanged = true;
      }
    }

    if (aiConfig.arrayItemImages) {
      const { arrayField, imageField } = aiConfig.arrayItemImages;
      const incomingItems = Array.isArray(section.props[arrayField])
        ? (section.props[arrayField] as Array<Record<string, unknown>>)
        : [];
      const storedItems = Array.isArray(storedSection.props[arrayField])
        ? (storedSection.props[arrayField] as Array<Record<string, unknown>>)
        : [];

      if (incomingItems.length > 0 && storedItems.length > 0) {
        const storedItemsById = new Map<string, Record<string, unknown>>();
        for (const storedItem of storedItems) {
          if (storedItem?.id) {
            storedItemsById.set(String(storedItem.id), storedItem);
          }
        }

        const nextItems = incomingItems.map((item) => {
          if (!item?.id) return item;
          const storedItem = storedItemsById.get(String(item.id));
          if (!storedItem) return item;

          const incomingValue = item[imageField];
          const storedValue = storedItem[imageField];

          if (isPendingAIImage(incomingValue) && storedValue) {
            const storedUrl = imageUrlForStoredValue(storedValue);
            if (!storedUrl) {
              return item;
            }
            sectionChanged = true;
            return {
              ...item,
              [imageField]: buildCompletedImageValue(incomingValue, storedUrl),
            };
          }

          return item;
        });

        if (sectionChanged) {
          nextProps = {
            ...nextProps,
            [arrayField]: nextItems,
          };
        }
      }
    }

    if (!sectionChanged) {
      return section;
    }

    hasChanges = true;
    return {
      ...section,
      props: nextProps,
    };
  });

  if (!hasChanges) {
    return incomingPage;
  }

  return {
    ...incomingPage,
    slots: {
      ...incomingPage.slots,
      main: {
        ...incomingPage.slots.main,
        sections: mergedSections,
      },
    },
  };
}

function imageUrlForStoredValue(value: unknown): string | undefined {
  const normalized = normalizeImageValue(value);
  return normalized?.url;
}
